let Cesium: any = null;
try {
  Cesium = require('cesium');
} catch {
  try {
    Cesium = (globalThis as any).Cesium || null;
  } catch {
    Cesium = null;
  }
}

export interface MICesiumIonConfig {
  token: string;
  serverUrl?: string;
}

export interface MICesiumIonTerrainOptions {
  token: string;
  assetId?: number;
  requestVertexNormals?: boolean;
  requestWaterMask?: boolean;
  serverUrl?: string;
}

export interface MICesiumIonTilesetOptions {
  token: string;
  assetId: number;
  serverUrl?: string;
  maximumScreenSpaceError?: number;
  show?: boolean;
}

export interface MICesiumIonImageryOptions {
  token: string;
  assetId: number;
  serverUrl?: string;
}

export class MICesiumIonProvider {
  private _token: string;
  private _serverUrl: string | undefined;

  constructor(config: MICesiumIonConfig) {
    this._token = config.token;
    this._serverUrl = config.serverUrl;

    if (Cesium && this._token) {
      Cesium.Ion.defaultAccessToken = this._token;
      if (this._serverUrl) {
        Cesium.Ion.defaultServer = this._serverUrl;
      }
    }
  }

  get token(): string {
    return this._token;
  }

  setToken(token: string): void {
    this._token = token;
    if (Cesium) {
      Cesium.Ion.defaultAccessToken = token;
    }
  }

  async terrain(options?: Partial<MICesiumIonTerrainOptions>): Promise<any> {
    const token = options?.token || this._token;
    const assetId = options?.assetId ?? 1;

    if (!Cesium) {
      throw new Error('3D engine is not available. Ensure the application is loaded in a WebGL-capable browser.');
    }

    if (!token) {
      throw new Error('A valid access token is required. Provide your token when creating the provider.');
    }

    try {
      const provider = await Cesium.CesiumTerrainProvider.fromIonAssetId(assetId, {
        accessToken: token,
        requestVertexNormals: options?.requestVertexNormals ?? true,
        requestWaterMask: options?.requestWaterMask ?? false,
      });
      return provider;
    } catch (err) {
      throw new Error(`Failed to load terrain (asset ${assetId}): ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async tileset(options: MICesiumIonTilesetOptions): Promise<any> {
    const token = options.token || this._token;

    if (!Cesium) {
      throw new Error('3D engine is not available. Ensure the application is loaded in a WebGL-capable browser.');
    }

    if (!token) {
      throw new Error('A valid access token is required. Provide your token when creating the provider.');
    }

    try {
      const resource = await Cesium.IonResource.fromAssetId(options.assetId, {
        accessToken: token,
      });
      const tileset = await Cesium.Cesium3DTileset.fromUrl(resource, {
        maximumScreenSpaceError: options.maximumScreenSpaceError ?? 16,
        show: options.show ?? true,
      });
      return tileset;
    } catch (err) {
      throw new Error(`Failed to load 3D tileset (asset ${options.assetId}): ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async imagery(options: MICesiumIonImageryOptions): Promise<any> {
    const token = options.token || this._token;

    if (!Cesium) {
      throw new Error('3D engine is not available. Ensure the application is loaded in a WebGL-capable browser.');
    }

    if (!token) {
      throw new Error('A valid access token is required. Provide your token when creating the provider.');
    }

    try {
      const provider = await Cesium.IonImageryProvider.fromAssetId(options.assetId, {
        accessToken: token,
      });
      return provider;
    } catch (err) {
      throw new Error(`Failed to load imagery (asset ${options.assetId}): ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  static worldTerrain(token: string, opts?: { requestWaterMask?: boolean; requestVertexNormals?: boolean }): Promise<any> {
    const provider = new MICesiumIonProvider({ token });
    return provider.terrain({
      assetId: 1,
      requestVertexNormals: opts?.requestVertexNormals ?? true,
      requestWaterMask: opts?.requestWaterMask ?? false,
    });
  }

  static bingAerial(token: string): Promise<any> {
    const provider = new MICesiumIonProvider({ token });
    return provider.imagery({ token, assetId: 2 });
  }

  static googlePhotorealistic(token: string): Promise<any> {
    const provider = new MICesiumIonProvider({ token });
    return provider.tileset({ token, assetId: 2275207 });
  }
}
